import React from 'react';
import { View, Text, ScrollView } from 'react-native';
import i18n from 'i18n-js';

import { scale } from '@components/frame';
import { isPhone } from '@helpers';

import Line from '@components/common/line';
import TouchableElement from '@components/common/touchableElement';
import { font } from '@styles/fonts';
import { getBoxShadowStyle } from '@styles/shadow';
import ColorWrapper from '@styles/colors/index';
import Svg from '@styles/icons/svg';
import FeatureFlags from '@components/featureFlags';

import Header from '@components/settings/v2/common/SettingsHeader';
import MobileHeader from './header';

import UserImage from '@components/common/UserImage';
import { SCREEN_BORDER_RADIUS, SCREEN_PADDING_HORIZONTAL, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { useManageAccount } from './hooks/useManageAccount';

const Account = (props) => {
  const {
    C,
    toggleChangeLanguage,
    toggleChangeTheme,
    lg,
    ColorsData: { custom },
    toggleInviteUser,
    selectedTab,
    toggleAddTool,
    toggleAddMachine,
    rootStack,
  } = props;

  const {
    firstName,
    lastName,
    emailAddress,
    companyName,
    featureFlags,
    filteredTabs,
    roundTopIndex,
    setup,
    actions,
    navigateToAccountDetails,
    navigateToSettingsSection,
  } = useManageAccount({ lg, custom, toggleChangeLanguage, toggleChangeTheme, toggleInviteUser, rootStack });

  const shadowStyles = getBoxShadowStyle({ type: 'Field', C });

  return (
    <View
      style={{
        width: '100%',
        flex: 1,
        height: '100%',
      }}
    >
      {isPhone ? (
        <MobileHeader {...{ C, tabs: filteredTabs, selectedTab, toggleInviteUser, toggleAddTool, toggleAddMachine, rootStack }} />
      ) : (
        <Header name={'Account'} />
      )}
      <ScrollView style={{ width: '100%', flex: 1 }} contentContainerStyle={{ paddingBottom: scale(50) }}>
        <View style={[{ paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL) }]}>
          <View
            testID={TEST_IDS.settingsScreen.accountProfileCard}
            style={[
              shadowStyles,
              {
                borderRadius: scale(SCREEN_BORDER_RADIUS),
              },
            ]}
          >
            <TouchableElement onPress={navigateToAccountDetails}>
              {({ boxStyle, pressHoverColor }) => (
                <View
                  style={[
                    boxStyle,
                    {
                      width: '100%',
                      height: scale(76),
                      backgroundColor: C(pressHoverColor('Background/Light/Field')),
                      paddingLeft: scale(15),
                      paddingRight: scale(20),
                      justifyContent: 'space-between',
                      borderRadius: scale(SCREEN_BORDER_RADIUS),
                      flexDirection: 'row',
                      alignContent: 'center',
                      alignItems: 'center',
                    },
                  ]}
                >
                  <UserImage {...{ showAdd: true, size: 56, style: { marginRight: scale(15) } }} />
                  <View style={{ flex: 1 }}>
                    <Text style={[font['Body'], { color: C('Label/Light/Primary') }]} numberOfLines={1}>
                      {firstName} {lastName}
                    </Text>
                    <Text style={[font['Footnote'], { color: C('Label/Light/Tertiary') }]} numberOfLines={1}>
                      {emailAddress}
                    </Text>
                    <Text style={[font['Subhead 1'], { color: C('Label/Light/Primary'), marginTop: scale(2) }]} numberOfLines={1}>
                      {companyName}
                    </Text>
                  </View>
                  <Svg icon="chevronRightLight" color="Label/Light/Quinary" width={scale(7)} height={scale(12)} />
                </View>
              )}
            </TouchableElement>
          </View>
          {actions.length > 0 && (
            <View
              style={[
                shadowStyles,
                {
                  borderRadius: scale(SCREEN_BORDER_RADIUS),
                  marginTop: scale(30),
                },
              ]}
            >
              {actions.map((item, index) => (
                <Line
                  {...{
                    item: {
                      ...item,
                      noArrow: true,
                      testID: TEST_IDS.settingsScreen.accountInviteColleague,
                    },
                    index,
                    array: actions,
                    C,
                    roundBottom: actions.length - 1 === index,
                    roundTop: index === 0,
                    topBorder: index !== 0,
                    textColor: 'Link/Light/Primary',
                  }}
                  key={item.name}
                />
              ))}
            </View>
          )}
          <View
            style={[
              shadowStyles,
              {
                borderRadius: scale(SCREEN_BORDER_RADIUS),
                marginTop: scale(30),
              },
            ]}
          >
            {setup.map((item, index) => (
              <Line
                {...{
                  item: {
                    ...item,
                    testID: index === 0 ? TEST_IDS.settingsScreen.accountLanguageLine : TEST_IDS.settingsScreen.accountThemeLine,
                  },
                  index,
                  array: setup,
                  C,
                  roundBottom: setup.length - 1 === index,
                  roundTop: index === 0,
                  topBorder: index !== 0,
                }}
                key={item.name}
              />
            ))}
          </View>
          {isPhone && (
            <View
              style={[
                shadowStyles,
                {
                  borderRadius: scale(SCREEN_BORDER_RADIUS),
                  marginTop: scale(30),
                },
              ]}
            >
              {filteredTabs.map((item, index) => (
                <FeatureFlags {...{ name: item.flag, C }} key={`${item.value}-${index}-item`}>
                  <Line
                    {...{
                      topBorder: index !== 0,
                      item: {
                        name: i18n.t(item.value),
                        value: '',
                        onClick: () => navigateToSettingsSection(item.value),
                      },
                      index,
                      array: filteredTabs,
                      C,
                      roundTop: index === roundTopIndex,
                      roundBottom: filteredTabs.length - 1 === index,
                    }}
                  />
                </FeatureFlags>
              ))}
            </View>
          )}
        </View>
        <View style={{ marginTop: scale(64), alignItems: 'center' }}>
          <Svg icon="SmartFactoryFill" color={C('Label/Light/Tertiary')} width={scale(145)} height={scale(70)} />
        </View>
      </ScrollView>
    </View>
  );
};

export default ColorWrapper(Account);
